// Defensive validation for user-supplied image sources (title-block logo,
// location-plan background). These values may come from untrusted input
// (imported .drawio files, share links, JSON templates) and are rendered as
// SVG/HTML attributes, so they must never be able to break out of the attribute
// or point at non-image protocols (javascript:, file:, ...).

const IMG_DATA_RE = /^data:image\/[a-z0-9.+-]+[;,]/i;
const HTTP_RE = /^https?:\/\/[^\s"'<>\\]+$/i;
// Any whitespace, quotes, angle brackets or control chars are never legal in a
// data: URI produced by FileReader/canvas.toDataURL and would only appear in an
// injection attempt.
// eslint-disable-next-line no-control-regex
const FORBIDDEN_RE = /[\s"'<>\\\u0000-\u001f\u007f]/;

/**
 * Returns the URI unchanged when it is a plain image data URI or an http(s)
 * URL, otherwise an empty string (image is simply not rendered).
 */
export function safeImageHref(uri) {
  if (typeof uri !== 'string') return '';
  const s = uri.trim();
  if (!s) return '';
  if (FORBIDDEN_RE.test(s)) return '';
  if (IMG_DATA_RE.test(s)) return s;
  if (HTTP_RE.test(s)) return s;
  return '';
}

/**
 * Colors are rendered as raw SVG attribute values / mxGraph style tokens.
 * Accept only #hex, named colors or rgb()/rgba()/hsl()/hsla() with numeric
 * arguments; anything else falls back to `fallback`.
 */
const COLOR_RE = /^(#[0-9a-f]{3,8}|[a-z]{3,20}|(rgb|hsl)a?\(\s*[\d.%]+\s*,\s*[\d.%]+\s*,\s*[\d.%]+\s*(,\s*[\d.]+\s*)?\))$/i;
export function safeColor(c, fallback = '#0B5FFF') {
  if (typeof c !== 'string') return fallback;
  const s = c.trim();
  return COLOR_RE.test(s) ? s : fallback;
}

/**
 * Sanitizes every image-bearing field of a document in place-safe fashion
 * (returns a new doc object; nested pages are only copied when changed).
 */
export function sanitizeDocImages(doc) {
  if (!doc || typeof doc !== 'object') return doc;
  let out = doc;
  const tb = doc.titleBlock;
  if (tb && typeof tb === 'object' && tb.logo !== undefined) {
    const logo = safeImageHref(tb.logo);
    if (logo !== tb.logo) out = { ...out, titleBlock: { ...tb, logo } };
  }
  if (Array.isArray(doc.vlans)) {
    let vChanged = false;
    const vlans = doc.vlans.map((v) => {
      if (!v || typeof v !== 'object' || v.color == null) return v;
      const color = safeColor(v.color);
      if (color === v.color) return v;
      vChanged = true;
      return { ...v, color };
    });
    if (vChanged) out = { ...out, vlans };
  }
  if (Array.isArray(doc.pages)) {
    let changed = false;
    const pages = doc.pages.map((p) => {
      if (!p || typeof p !== 'object') return p;
      let np = p;
      if (p.type === 'location' && p.background) {
        const uri = safeImageHref(p.background.dataUri);
        if (uri !== (p.background.dataUri || '')) np = { ...np, background: { ...p.background, dataUri: uri } };
      }
      if (Array.isArray(p.lags) && p.lags.some((l) => l && l.color != null && l.color !== safeColor(l.color))) {
        np = { ...np, lags: p.lags.map((l) => (l && typeof l === 'object' && l.color != null ? { ...l, color: safeColor(l.color) } : l)) };
      }
      if (np !== p) changed = true;
      return np;
    });
    if (changed) out = { ...out, pages };
  }
  return out;
}

export const sanitizeDoc = sanitizeDocImages;
